import {useCallback, useEffect, useMemo, useState} from 'react';

import debounce from 'lodash.debounce';
import {useReadOnlyDataHook} from 'src/hooks/use-read-only-data-hook';
import {getMySavedLists} from 'src/openapi/admin/apis/SearchSavedSearchApi';
import {DateRangeFilter} from 'src/openapi/admin/models/DateRangeFilter';
import {SavedListRequestSortField} from 'src/openapi/admin/models/SavedListRequest';
import {SavedListType} from 'src/openapi/admin/models/SavedListType';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {useAppDispatch} from 'src/store/hooks';

import {DEFAULT_PAGE_SIZE} from '../constants';


type Props = {
    userId: number,
};

export const useGetSavedLists = ({userId}: Props) => {
    const apiDispatch = useAppDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<string>('');

    const [searchType, setSearchType] = useState<SavedListType | ''>('');

    const [sortField, setSortField] = useState<SavedListRequestSortField>(SavedListRequestSortField.NAME);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);
    const [typeFilter, setTypeFilter] = useState<SavedListType[]>([]);
    const [createDateFilter, setCreateDateFilter] = useState<DateRangeFilter | undefined>(undefined);
    const [lastRunDateFilter, setLastRunDateFilter] = useState<DateRangeFilter | undefined>(undefined);
    const [lastSharedDateFilter, setLastSharedDateFilter] = useState<DateRangeFilter | undefined>(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetQuery = useCallback(debounce((query: string) => {
        setDebouncedQuery(query);
    }, 1000), []);

    useEffect(() => {
        debouncedSetQuery(searchQuery.trim());
    }, [searchQuery, debouncedSetQuery]);

    const isEmptySearchCriteria = useMemo(() => {
        const isQueryEmpty = !searchQuery || searchQuery.trim().length === 0;
        const isSearchTypeEmpty = !searchType;
        const isTypeFilterEmpty = !typeFilter || typeFilter.length === 0;
        const isCreateDateEmpty = !createDateFilter;

        return (
            isQueryEmpty &&
            isSearchTypeEmpty &&
            isTypeFilterEmpty &&
            isCreateDateEmpty
        );
    }, [searchQuery, searchType, typeFilter, createDateFilter]);

    const getDispatchAction = useCallback(() => {
        let searchTypes: SavedListType[] | undefined;
        if (typeFilter.length > 0) {
            searchTypes = typeFilter;
        } else if (searchType) {
            searchTypes = [searchType];
        } else {
            searchTypes = undefined;
        }

        return apiDispatch(
            getMySavedLists({userId,
                savedListRequest: {
                    pagination: {currentPage, pageSize},
                    sortDirection,
                    sortField,
                    nameQuery: debouncedQuery || '',
                    searchTypes,
                    createDateFilter,
                    lastRunDateFilter,
                    lastSharedDateFilter,
                }}),
        );
    }, [userId,
        currentPage,
        pageSize,
        apiDispatch,
        debouncedQuery,
        searchType,
        sortDirection,
        sortField,
        typeFilter,
        createDateFilter,
        lastSharedDateFilter,
        lastRunDateFilter]);

    const {data, isLoading, loadData, loadingError} = useReadOnlyDataHook({
        initValue: {
            searches: [],
            totalCount: 0,
        },
        getDispatchAction,
        shouldLoadData: false,
    });

    useEffect(() => {
        loadData();
    }, [currentPage,
        pageSize,
        loadData,
        debouncedQuery,
        searchType,
        sortDirection,
        sortField,
        typeFilter,
        createDateFilter,
        lastRunDateFilter,
        lastSharedDateFilter]);

    return {
        data,
        isLoading,
        load: loadData,
        loadingError,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        searchQuery,
        setSearchQuery,
        searchType,
        setSearchType,
        // Sort and filter controls
        sortField,
        setSortField,
        sortDirection,
        setSortDirection,
        typeFilter,
        setTypeFilter,
        createDateFilter,
        setCreateDateFilter,
        lastRunDateFilter,
        setLastRunDateFilter,
        lastSharedDateFilter,
        setLastSharedDateFilter,
        totalCount: data?.totalCount || 0,
        isEmptySearchCriteria,
    };
};
